import { normalizeDevServerStatus, normalizeYoloFlowStep } from "@/lib/thread-sync-control-plane"
import {
  normalizePendingQueueRecord,
  normalizeRejectionRecoveryFlag,
} from "@/lib/thread-sync-message-normalizers"
import {
  dispatchSessionFileChangesUpdated,
  dispatchSessionFileTreeUpdated,
} from "@/lib/session-file-sync-events"
import {
  dispatchSessionPrStatusUpdated,
  dispatchSessionQuickPrCompleted,
} from "@/lib/session-pr-sync-events"
import type { SessionPrStatusUpdatedDetail } from "@/lib/session-pr-sync-events"
import { dispatchSessionBranchUpdated } from "@/lib/session-branch-sync-events"
import { dispatchSessionStartNewWorkGitCompleted } from "@/lib/session-start-new-work-events"
import {
  applySessionGitExecResult,
  normalizeSessionGitExecResult,
} from "@/lib/session-git-exec-result"
import {
  dedupePendingQueueRecords,
} from "@/lib/session-queue-ui-state"
import type {
  LocalOutboxQueueRecord,
  PendingQueueRecord,
} from "@/lib/session-queue-ui-state"
import type {
  CodeServerStatus,
  MessageRejectionEvent,
  SandboxProgress,
  YoloFlowStep,
} from "@/hooks/thread-sync-client-types"
import {
  getSessionPrFlowSnapshotTimestamp,
  normalizeSessionPrFlowSnapshot,
  useSessionPrFlow,
} from "@/stores/session-pr-flow"
import { useSessionPrActionState } from "@/stores/session-pr-action-state"
import {
  useThreadRuntime,
  type ApplyThreadStatusResult,
} from "@/stores/thread-runtime"

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null
  return value as Record<string, unknown>
}

function asString(value: unknown): string | null {
  if (typeof value !== "string") return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function asNumber(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null
  return Math.floor(value)
}

function asPositiveNumber(value: unknown): number | null {
  const normalized = asNumber(value)
  if (normalized === null || normalized <= 0) return null
  return normalized
}

export function normalizePendingQueueRecords(rawPendingMessages: unknown): PendingQueueRecord[] {
  if (!Array.isArray(rawPendingMessages)) {
    return []
  }

  return dedupePendingQueueRecords({
    messages: rawPendingMessages
      .map((value) => normalizePendingQueueRecord(value))
      .filter((value): value is PendingQueueRecord => value !== null),
  })
}

export function getLatestPendingQueueTimestamp(args: {
  pendingQueueRecords: PendingQueueRecord[]
}): number | null {
  let latestPendingQueueTimestamp: number | null = null

  for (const pendingQueueRecord of args.pendingQueueRecords) {
    const pendingQueueTimestamp = asPositiveNumber(pendingQueueRecord.timestamp)
    if (pendingQueueTimestamp === null) {
      continue
    }
    latestPendingQueueTimestamp = latestPendingQueueTimestamp === null
      ? pendingQueueTimestamp
      : Math.max(latestPendingQueueTimestamp, pendingQueueTimestamp)
  }

  return latestPendingQueueTimestamp
}

export function reconcileLocalOutboxWithServerPending(args: {
  localOutbox: LocalOutboxQueueRecord[]
  serverPendingMessages: PendingQueueRecord[]
  preserveClientMessageIds?: Iterable<string>
}): LocalOutboxQueueRecord[] {
  if (args.localOutbox.length === 0) {
    return args.localOutbox
  }

  const serverPendingByClientMessageId = new Map<string, PendingQueueRecord>()
  for (const message of args.serverPendingMessages) {
    if (typeof message.clientMessageId !== "string" || message.clientMessageId.length === 0) {
      continue
    }
    serverPendingByClientMessageId.set(message.clientMessageId, message)
  }
  const pendingClientMessageIds = new Set(serverPendingByClientMessageId.keys())
  if (args.preserveClientMessageIds) {
    for (const clientMessageId of args.preserveClientMessageIds) {
      pendingClientMessageIds.add(clientMessageId)
    }
  }

  return args.localOutbox
    .filter((message) => pendingClientMessageIds.has(message.clientMessageId))
    .map((message) => {
      const serverPendingMessage = serverPendingByClientMessageId.get(message.clientMessageId)
      if (!serverPendingMessage) return message
      if (
        message.observedServerStatus === serverPendingMessage.status &&
        message.renderedHandoffExpiresAt === undefined
      ) {
        return message
      }
      if (typeof message.renderedHandoffExpiresAt === "number") {
        return {
          ...message,
          observedServerStatus: message.observedServerStatus ?? serverPendingMessage.status,
        }
      }
      return {
        ...message,
        observedServerStatus: serverPendingMessage.status,
        renderedHandoffExpiresAt: undefined,
      }
    })
}

export function removeRenderedMessageFromQueueState(args: {
  serverPendingMessages: PendingQueueRecord[]
  localOutbox: LocalOutboxQueueRecord[]
  messageId: unknown
  clientMessageId: unknown
  localHandoffExpiresAt?: number
}): {
  serverPendingMessages: PendingQueueRecord[]
  localOutbox: LocalOutboxQueueRecord[]
  removedClientMessageId: string | null
} {
  const messageId = asNumber(args.messageId)
  const clientMessageId = asString(args.clientMessageId)
  const matchingServerPendingMessage = messageId === null
    ? null
    : args.serverPendingMessages.find((item) => item.id === messageId) ?? null
  const resolvedClientMessageId = clientMessageId ?? (
    messageId === null
      ? null
      : asString(matchingServerPendingMessage?.clientMessageId)
  )

  let serverPendingMessages = args.serverPendingMessages
  let localOutbox = args.localOutbox

  if (messageId !== null) {
    serverPendingMessages = serverPendingMessages.filter((item) => item.id !== messageId)
  }

  if (resolvedClientMessageId) {
    serverPendingMessages = serverPendingMessages.filter(
      (item) => item.clientMessageId !== resolvedClientMessageId,
    )
    if (typeof args.localHandoffExpiresAt === "number" && Number.isFinite(args.localHandoffExpiresAt)) {
      localOutbox = localOutbox.map((item) => {
        if (item.clientMessageId !== resolvedClientMessageId) return item
        return {
          ...item,
          observedServerStatus: item.observedServerStatus ?? matchingServerPendingMessage?.status ?? "accepted",
          renderedHandoffExpiresAt: args.localHandoffExpiresAt,
        }
      })
    } else {
      localOutbox = localOutbox.filter((item) => item.clientMessageId !== resolvedClientMessageId)
    }
  }

  return {
    serverPendingMessages,
    localOutbox,
    removedClientMessageId: resolvedClientMessageId,
  }
}

export function applyRejectedMessageToQueueState(args: {
  serverPendingMessages: PendingQueueRecord[]
  localOutbox: LocalOutboxQueueRecord[]
  message: { type: string; [key: string]: unknown }
  nowMs: number
}): {
  serverPendingMessages: PendingQueueRecord[]
  localOutbox: LocalOutboxQueueRecord[]
  rejection: MessageRejectionEvent
} {
  const clientMessageId = asString(args.message.clientMessageId)

  const localOutbox = clientMessageId
    ? args.localOutbox.filter((item) => item.clientMessageId !== clientMessageId)
    : args.localOutbox

  const serverPendingMessages = clientMessageId
    ? args.serverPendingMessages.filter((item) => item.clientMessageId !== clientMessageId)
    : args.serverPendingMessages

  return {
    serverPendingMessages,
    localOutbox,
    rejection: {
      reason: asString(args.message.code) ?? "message_rejected",
      conversationId: asString(args.message.conversationId) ?? "default",
      message: asString(args.message.message),
      timestamp: asNumber(args.message.timestamp) ?? args.nowMs,
      retryable: normalizeRejectionRecoveryFlag(args.message.retryable),
      recovery: null,
    },
  }
}

export function normalizeCodeServerStatusSideChannel(args: {
  status: unknown
  error: unknown
  vscodeUrl: unknown
}): {
  codeServerStatus: CodeServerStatus
  codeServerError: string | null
  vscodeUrl: string | null
} {
  return {
    codeServerStatus: normalizeDevServerStatus(args.status) as CodeServerStatus,
    codeServerError: asString(args.error),
    vscodeUrl: asString(args.vscodeUrl),
  }
}

export function normalizeSandboxProgressMessage(args: {
  step: unknown
  message: unknown
  timestamp: unknown
  nowMs: number
}): SandboxProgress | null {
  const step = asString(args.step)
  const message = asString(args.message)
  if (!step || !message) {
    return null
  }

  return {
    step,
    message,
    timestamp: asNumber(args.timestamp) ?? args.nowMs,
  }
}

export function appendSandboxProgressEntries(args: {
  current: SandboxProgress[]
  next: SandboxProgress
}): SandboxProgress[] {
  return [...args.current, args.next].slice(-50)
}

export type YoloFlowMergeFailureKind =
  | "conflicting"
  | "behind"
  | "blocked"
  | "draft"
  | "unknown"

export function computeYoloFlowUpdate(args: {
  currentUpdatedAt: number | null
  currentStep: YoloFlowStep
  currentError: string | null
  step: unknown
  error: unknown
  timestamp: unknown
  mergeFailureKind?: unknown
  treatNonPositiveTimestampAsMissing?: boolean
}): {
  applied: boolean
  nextStep: YoloFlowStep
  nextError: string | null
  nextUpdatedAt: number | null
  timestamp: number | null
  mergeFailureKind: YoloFlowMergeFailureKind | null
} {
  const nextStep = normalizeYoloFlowStep(args.step)
  const nextError = asString(args.error)
  const nextTimestamp = args.treatNonPositiveTimestampAsMissing === true
    ? asPositiveNumber(args.timestamp)
    : asNumber(args.timestamp)
  const mergeFailureKind = normalizeYoloMergeFailureKind(args.mergeFailureKind)

  if (
    nextTimestamp !== null &&
    args.currentUpdatedAt !== null &&
    nextTimestamp < args.currentUpdatedAt
  ) {
    return {
      applied: false,
      nextStep: args.currentStep,
      nextError: args.currentError,
      nextUpdatedAt: args.currentUpdatedAt,
      timestamp: nextTimestamp,
      mergeFailureKind: null,
    }
  }

  return {
    applied: true,
    nextStep,
    nextError,
    nextUpdatedAt: nextTimestamp ?? args.currentUpdatedAt,
    timestamp: nextTimestamp,
    mergeFailureKind: nextStep === "error" ? mergeFailureKind : null,
  }
}

const YOLO_MERGE_FAILURE_KINDS = new Set<string>([
  "conflicting",
  "behind",
  "blocked",
  "draft",
  "unknown",
])

function normalizeYoloMergeFailureKind(value: unknown): YoloFlowMergeFailureKind | null {
  if (typeof value !== "string") return null
  const trimmed = value.trim()
  return YOLO_MERGE_FAILURE_KINDS.has(trimmed)
    ? (trimmed as YoloFlowMergeFailureKind)
    : null
}

export function applyThreadStatusSideEffects(args: {
  sessionId: string
  threadStatus: {
    activityState: string | null
    lastActivityEventId: number | null
    lastFinishedEventId: number | null
    lastFinishedAt: number | null
    lastFinishReason: string | null
    sandboxStatus: string | null
    persistedStatus: string | null
    lifecycleStatus: string | null
  }
  observedAt?: number
}): ApplyThreadStatusResult {
  return useThreadRuntime.getState().applyThreadStatus({
    sessionId: args.sessionId,
    activityState: args.threadStatus.activityState,
    lastActivityEventId: args.threadStatus.lastActivityEventId,
    lastFinishedEventId: args.threadStatus.lastFinishedEventId,
    lastFinishedAt: args.threadStatus.lastFinishedAt,
    lastFinishReason: args.threadStatus.lastFinishReason,
    sandboxStatus: args.threadStatus.sandboxStatus,
    persistedStatus: args.threadStatus.persistedStatus,
    lifecycleStatus: args.threadStatus.lifecycleStatus,
    observedAt: args.observedAt,
  })
}

export function applyPendingMessageCountSideEffects(args: {
  sessionId: string
  pendingMessageCount: number
  latestPendingWorkTimestamp?: number | null
}): void {
  useThreadRuntime.getState().setPendingMessageCount({
    sessionId: args.sessionId,
    pendingMessageCount: args.pendingMessageCount,
    latestPendingWorkTimestamp: args.latestPendingWorkTimestamp,
  })
}

export function clearOptimisticWorkRequestPendingSideEffects(args: {
  sessionId: string
}): void {
  useThreadRuntime.getState().setOptimisticWorkRequestPending({
    sessionId: args.sessionId,
    pending: false,
  })
}

export function applyYoloFlowSideEffects(args: {
  sessionId: string
  step: YoloFlowStep
  error?: string | null
  timestamp: number | null
  mergeFailureKind?: YoloFlowMergeFailureKind | null
}): void {
  useThreadRuntime.getState().setYoloFlowStep({
    sessionId: args.sessionId,
    step: args.step,
    timestamp: args.timestamp,
  })

  // When YOLO fails at the merge step with a classified failure, propagate it
  // to the PR action state store so the header action state machine can show
  // "Resolve Conflicts" (or the appropriate state) instead of "Merge".
  // This is critical for reconnection scenarios where the client missed the
  // original `merge_pr_result` WebSocket message because it was on another
  // thread.
  if (args.step === "error" && args.mergeFailureKind) {
    useThreadRuntime.getState().applyThreadPrMergeFailureHint({
      sessionId: args.sessionId,
      mergeFailureKind: args.mergeFailureKind,
      observedAt: args.timestamp,
    })
    useSessionPrActionState.getState().settleMergeRequest({
      sessionId: args.sessionId,
      requestId: null,
      ok: false,
      error:
        typeof args.error === "string" && args.error.trim().length > 0
          ? args.error.trim()
          : "YOLO merge failed",
      mergeConflict: args.mergeFailureKind === "conflicting",
      failureKind: args.mergeFailureKind,
      timestamp: typeof args.timestamp === "number" ? args.timestamp : undefined,
    })
  }
}

export function applyPrFlowSnapshotSideEffects(args: {
  sessionId: string
  rawSnapshot: unknown
}): boolean {
  const snapshot = normalizeSessionPrFlowSnapshot(args.rawSnapshot)
  const appliedSnapshot = useSessionPrFlow.getState().setSnapshot(args.sessionId, snapshot)
  if (!appliedSnapshot) {
    return false
  }

  const snapshotTimestamp = getSessionPrFlowSnapshotTimestamp(snapshot)
  const appliedRuntimeOperation = useThreadRuntime.getState().setPrFlowOperation({
    sessionId: args.sessionId,
    kind: snapshot?.activeOperation?.kind ?? "none",
    status: snapshot?.activeOperation?.status ?? "idle",
    step: snapshot?.activeOperation?.step ?? "idle",
    timestamp: snapshotTimestamp,
  })

  const entity = snapshot?.entity
  if (
    entity &&
    typeof entity.prNumber === "number" &&
    (entity.prState === "draft" ||
      entity.prState === "open" ||
      entity.prState === "merged" ||
      entity.prState === "closed")
  ) {
    dispatchSessionPrStatusUpdated({
      sessionId: args.sessionId,
      prNumber: entity.prNumber,
      prUrl: entity.prUrl,
      prState: entity.prState,
      headBranch: asString(entity.headBranch),
      baseBranch: asString(entity.baseBranch),
      mergeable: entity.mergeable,
      mergeableState: entity.mergeableState,
      mergeabilityKnown:
        typeof entity.mergeable === "boolean" ||
        (typeof entity.mergeableState === "string" && entity.mergeableState.trim().length > 0),
      timestamp: entity.lastSyncedAt ?? snapshotTimestamp,
    })
  }

  return appliedRuntimeOperation
}

export function applyQuickPrCompletionSideEffects(args: {
  sessionId: string
  eventName: unknown
  operationKind: unknown
}): void {
  if (
    (args.eventName !== "operation_completed" && args.eventName !== "operation_reconciled") ||
    (args.operationKind !== "quick_pr" && args.operationKind !== "update_pr")
  ) {
    return
  }

  dispatchSessionQuickPrCompleted({ sessionId: args.sessionId })
}

export function applyResolveConflictSideEffects(args: {
  sessionId: string
  requestId: string | null
  conversationId?: string | null
  state: "started" | "dispatched" | "failed"
  baseBranch: string | null
  dispatchMode?: "queued" | "forwarded" | null
  error?: string | null
  timestamp: number
}): void {
  useSessionPrActionState.getState().setResolveConflictFromServer({
    sessionId: args.sessionId,
    requestId: args.requestId,
    conversationId: args.conversationId,
    state: args.state,
    baseBranch: args.baseBranch,
    dispatchMode: args.dispatchMode,
    error: args.error,
    timestamp: args.timestamp,
  })
}

export function clearResolveConflictSideEffects(args: {
  sessionId: string
  conversationId?: string | null
  timestamp: number
  observedAt?: number
}): void {
  useSessionPrActionState.getState().clearResolveConflictFromStatus({
    sessionId: args.sessionId,
    conversationId: args.conversationId,
    timestamp: args.timestamp,
    observedAt: args.observedAt,
  })
}

export function applyPrStatusSideEffects(args: {
  sessionId: string
  messageData: unknown
}): void {
  const prData = asRecord(args.messageData)
  const prNumber = asNumber(prData?.prNumber)
  const prState = asString(prData?.prState)
  if (
    prNumber === null ||
    (prState !== "draft" && prState !== "open" && prState !== "merged" && prState !== "closed")
  ) {
    return
  }

  const detail: SessionPrStatusUpdatedDetail = {
    sessionId: args.sessionId,
    prNumber,
    prUrl: asString(prData?.prUrl),
    prState,
    headBranch: asString(prData?.headBranch),
    baseBranch: asString(prData?.baseBranch),
    timestamp: asNumber(prData?.timestamp),
  }
  if (
    Object.prototype.hasOwnProperty.call(prData, "mergeable") ||
    Object.prototype.hasOwnProperty.call(prData, "mergeableState")
  ) {
    dispatchSessionPrStatusUpdated({
      ...detail,
      mergeable: typeof prData?.mergeable === "boolean" ? prData.mergeable : null,
      mergeableState: asString(prData?.mergeableState),
      mergeabilityKnown: prData?.mergeabilityKnown === true,
    })
    return
  }

  dispatchSessionPrStatusUpdated(detail)
}

export function applyMergePrResultSideEffects(args: {
  sessionId: string
  message: { type: string; [key: string]: unknown }
  nowMs: number
}): void {
  const mergeFailureKind = asString(args.message.mergeFailureKind)
  const normalizedMergeFailureKind =
    mergeFailureKind === "conflicting" ||
    mergeFailureKind === "behind" ||
    mergeFailureKind === "blocked" ||
    mergeFailureKind === "draft" ||
    mergeFailureKind === "unknown"
      ? mergeFailureKind
      : null

  if (args.message.ok === true) {
    const prNumber = asNumber(args.message.prNumber)
    if (prNumber !== null) {
      dispatchSessionPrStatusUpdated({
        sessionId: args.sessionId,
        prNumber,
        prUrl: null,
        prState: "merged",
        timestamp: asNumber(args.message.timestamp),
      })
    }
  }

  if (args.message.ok !== true && normalizedMergeFailureKind) {
    useThreadRuntime.getState().applyThreadPrMergeFailureHint({
      sessionId: args.sessionId,
      mergeFailureKind: normalizedMergeFailureKind,
      observedAt: asNumber(args.message.timestamp) ?? args.nowMs,
    })
  }

  useSessionPrActionState.getState().settleMergeRequest({
    sessionId: args.sessionId,
    requestId: asString(args.message.requestId),
    ok: args.message.ok === true,
    error: args.message.ok === true ? null : (asString(args.message.error) ?? "Failed to merge PR"),
    mergeConflict: args.message.mergeConflict === true,
    failureKind: normalizedMergeFailureKind,
    timestamp: asNumber(args.message.timestamp) ?? args.nowMs,
  })
}

export function applyResolveConflictSideChannelMessage(args: {
  sessionId: string
  message: { type: string; [key: string]: unknown }
  state: "started" | "dispatched" | "failed"
  nowMs: number
}): void {
  const dispatchMode = asString(args.message.dispatchMode)

  applyResolveConflictSideEffects({
    sessionId: args.sessionId,
    requestId: asString(args.message.requestId),
    conversationId: asString(args.message.conversationId),
    state: args.state,
    baseBranch: asString(args.message.baseBranch),
    dispatchMode:
      args.state === "dispatched" && (dispatchMode === "queued" || dispatchMode === "forwarded")
        ? dispatchMode
        : null,
    error:
      args.state === "failed"
        ? (asString(args.message.error) ?? "Failed to start conflict resolution")
        : null,
    timestamp: asNumber(args.message.timestamp) ?? args.nowMs,
  })
}

export function applyFileTreeSideEffects(args: {
  sessionId: string
  data: unknown
}): void {
  const data = asRecord(args.data)
  if (!data) {
    return
  }

  dispatchSessionFileTreeUpdated({
    sessionId: args.sessionId,
    data: data as never,
  })
}

export function applyFileChangesSideEffects(args: {
  sessionId: string
  data: unknown
}): void {
  const data = asRecord(args.data)
  if (!data) {
    return
  }

  dispatchSessionFileChangesUpdated({
    sessionId: args.sessionId,
    data: data as never,
  })
}

export function applyGitResultSideEffects(args: {
  sessionId: string
  message: { type: string; [key: string]: unknown }
}): void {
  const result = normalizeSessionGitExecResult({ message: args.message })
  if (!result) {
    return
  }

  applySessionGitExecResult({
    sessionId: args.sessionId,
    result,
  })
}

export function applySessionBranchUpdatedSideEffects(args: {
  sessionId: string
  message: { type: string; [key: string]: unknown }
}): void {
  const branch = asString(args.message.branch)
  if (!branch) return

  dispatchSessionBranchUpdated({
    sessionId: args.sessionId,
    branch,
    baseBranch: asString(args.message.baseBranch),
    clearBranchSwitchInProgress: args.message.clearBranchSwitchInProgress === true,
  })
}

export function applyStartNewWorkGitCompletedSideEffects(args: {
  sessionId: string
  message: { type: string; [key: string]: unknown }
}): void {
  dispatchSessionStartNewWorkGitCompleted({
    sessionId: args.sessionId,
    ok: args.message.ok === true,
    newBranch: asString(args.message.newBranch),
    baseBranch: asString(args.message.baseBranch),
    error: asString(args.message.error),
  })
}
